/** Month/year chip helpers for History and History(L) date filters. */

export function historyCurrentMonthKey(at: Date = new Date()): string {
  return historyFactDateKey(at).slice(0, 7);
}

/** Today in US/Eastern as YYYY-MM-DD (matches server cash-flow fact filter). */
export function historyFactDateKey(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  if (!year || !month || !day) return at.toISOString().slice(0, 10);
  return `${year}-${month}-${day}`;
}

export function historyMonthShortLabel(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "short", timeZone: "UTC" });
}

export function historyMonthChipClassName(
  monthKey: string,
  isSelected: boolean,
  inactive = false,
): string {
  const cm = historyCurrentMonthKey();
  let toneClass = "sfe-month-chip--blue";
  if (monthKey < cm) toneClass = "sfe-month-chip--past";
  else if (monthKey === cm) toneClass = "sfe-month-chip--current";
  const classes = ["sfe-income-month-chip", toneClass];
  if (inactive) classes.push("sfe-month-chip--inactive");
  if (isSelected) classes.push("active");
  return classes.join(" ");
}

export function historyYearChipClassName(year: number | "all", isSelected: boolean): string {
  const classes = ["year-chip", "sfe-income-year-chip"];
  if (year === "all") {
    classes.push("sfe-year-chip--all");
    if (isSelected) classes.push("active");
    return classes.join(" ");
  }
  const nowYear = new Date().getFullYear();
  if (year < nowYear) classes.push("sfe-year-chip--past");
  else if (year === nowYear) classes.push("sfe-year-chip--current");
  else classes.push("sfe-year-chip--blue");
  if (isSelected) classes.push("active");
  return classes.join(" ");
}

export function historyMonthKeyForTxn(txn: {
  trade_date?: string | null;
  instrument_type?: string | null;
  cash_flow?: boolean;
  extensions?: Record<string, string> | null;
}): string {
  const isCash = txn.instrument_type === "cash" || txn.cash_flow === true;
  const brokerRef = String(txn.extensions?.broker_ref ?? "").trim();
  if (isCash && /^\d{4}-\d{2}$/.test(brokerRef)) return brokerRef;
  return String(txn.trade_date ?? "").slice(0, 7);
}

/** Year whose 12 month chips are shown. All / range filters use the current
 *  calendar year when it is on record, otherwise the newest year. */
export function historyMonthRowYear(
  selectedYear: number | "all" | "7d" | "30d",
  years: number[],
): number {
  if (typeof selectedYear === "number") return selectedYear;
  const nowYear = new Date().getFullYear();
  if (years.includes(nowYear)) return nowYear;
  return years[0] ?? nowYear;
}

export function monthsWithTransactions(
  transactions: Array<{
    trade_date?: string | null;
    instrument_type?: string | null;
    cash_flow?: boolean;
    extensions?: Record<string, string> | null;
  }>,
  year: number,
): Set<string> {
  const months = new Set<string>();
  const prefix = `${year}-`;
  for (const txn of transactions) {
    const monthKey = historyMonthKeyForTxn(txn);
    if (monthKey.startsWith(prefix)) months.add(monthKey);
  }
  return months;
}

export function monthKeysForYear(year: number): string[] {
  return Array.from({ length: 12 }, (_, index) => {
    const m = String(12 - index).padStart(2, "0");
    return `${year}-${m}`;
  });
}

export function resolveDefaultHistoryMonth(year: number, activeMonths: Set<string>): string | null {
  if (!activeMonths.size) return null;
  const cm = historyCurrentMonthKey();
  if (cm.startsWith(`${year}-`) && activeMonths.has(cm)) return cm;
  return [...activeMonths].sort().at(-1) ?? null;
}
